import isNumber from 'lodash/isNumber';

import pseudolocalize from '~/utils/pseudolocalize';
import { isProd, isPseudolocalizationEnabled } from '~/utils/EnvUtils';
import Constants from '~/config/Constants';

import $store from './StoreService';
import QLogger from './QLogger';
import { useTranslation } from 'i18next-vue';

export type Language = 'qpsl' | 'en' | 'es' | 'fr' | 'pl' | 'ru' | 'id' | 'ms' | 'tl' | 'de' | 'vi' | 'th' | 'az' | 'pt-br' | 'zh-CN' | 'zh-TW' | 'tr' | 'it' | 'ja' | 'kk';

// TODO: UPDATE DB AND REMOVE THIS CONSTANT
const paramReplacementNumber: number = 192809182;
class Translator {
  private static currentLanguage: Language = 'en';

  public setCurrentLanguage: (lang: Language) => void;

  public getTranslation: (key: string, values?: any, maxLength?: number) => string;

  public getCurrentLanguage: () => Language;

  constructor() {
    this.setCurrentLanguage = function setCurrentLanguage(lang: Language): void {
      QLogger.invariant(
        !!lang,
        `Translator: invalid language '${lang}' provided to setCurrentLanguage`,
      );
      try {
        Translator.currentLanguage = lang;
      } catch (error) {
        QLogger.error(error);
      }
    };

    this.getTranslation = (key: string, values?: any, maxLength?: number): string => {
      const stringifiedKey = String(key); // force conversion to string in case of numbers or .includes won't work
      const currentLocale = Translator.currentLanguage;
      let valuesToUse = values;
      let maxLengthToUse = maxLength;

      if (!isNumber(maxLength) && isNumber(values)) {
        maxLengthToUse = values;
        valuesToUse = [];
      }

      return Translator.translate(
        stringifiedKey,
        currentLocale,
        valuesToUse,
        maxLengthToUse,
      );
    };

    this.getCurrentLanguage = (): Language => Translator.currentLanguage;
  }

  public static substituteParameters(string: string, params: Record<string, any>) {
    if (!params) {
      return string;
    }

    let result = string;

    let i = 0;
    // regex to extract parameters (like {$1}, {$2}) from the string and replace them with the params
    const paramRegex = /(\{\$.*?\})/;

    // remove the constant from string and replace it with param placeholders
    const reverseParamRegex = new RegExp(paramReplacementNumber.toString());
    let paramNumber = 0;
    while (reverseParamRegex.exec(result)) {
      result = result.replace(reverseParamRegex, `{$${paramNumber}}`);
      paramNumber += 1;
    }
    let matches = paramRegex.exec(result);
    while (matches) {
      if (params[i] === undefined) {
        result = result.replace(paramRegex, '');
      } else if (isPseudolocalizationEnabled() || (this.currentLanguage === Constants.PSEUDOLOCALIZE_CODE && !isProd())) {
        result = result.replace(paramRegex, `[{${params[i]}}]`);
      } else {
        result = result.replace(paramRegex, params[i]);
      }

      i += 1;
      matches = paramRegex.exec(result);
    }
    // if (import.meta.env.VITE_ENV_API_EXP === 'dev') {
    //   const str = result.split('').join('\u0301');
    //   return `[${str} one]`;
    // }
    return result;
  }

  public static translate(string: string, locale = 'en', values = [], maxLength?: number): string {
    let maximumStringLength = maxLength;
    if (maximumStringLength === undefined || maximumStringLength === 0) {
      maximumStringLength = -1;
    }

    if ((isPseudolocalizationEnabled()) || (locale === Constants.PSEUDOLOCALIZE_CODE && !isProd())) {
      const str = pseudolocalize(string);
      return Translator.substituteParameters(str, values);
    }

    if (locale === 'en') {
      return Translator.substituteParameters(string, values);
    }

    let translations: Record<string, any> = {} as Record<string, any>;
    let englishStringMap: Record<string, any> = {} as Record<string, any>;
    try {
      translations = $store() ? $store().getters['root/translations'] : null;
      if (!translations) {
        return Translator.substituteParameters(string, values);
      }
      englishStringMap = $store() ? $store().getters['root/englishMaps'] : {};
    } catch (err) {
      QLogger.warn('Translation JSON files were not found ');

      return string;
    }

    // translate params if available
    const params = values.map((p) => {
      if (`${p}` in englishStringMap) {
        return translations[englishStringMap[p]];
      }
      return p;
    });

    let stringId = '';

    if (`${string}` in englishStringMap) {
      stringId = englishStringMap[`${string}`];
    } else {
      return Translator.substituteParameters(string, params);
    }

    if (stringId in translations) {
      return Translator.substituteParameters(translations[stringId], params);
    }

    return string;
  }
}
export const translator = new Translator();
const i18n = translator.getTranslation.bind(translator);

export default i18n;

// i18next instance for JS/TS files
let _i18next: any;
export function $t(...args: any): string {
  if (!_i18next) {
    _i18next = useTranslation().t;
  }
  return _i18next(...args);
}
